/**
 * AnimatedStat - KPI stat component
 *
 * CRITICAL FIX: Always renders the FINAL value immediately on mount.
 * Animation (Matrix scramble → count-up) is a progressive enhancement
 * that only runs when the element enters the viewport via IntersectionObserver.
 * If IO never fires (iOS Safari overflow clip, prerender, etc.) the final
 * value is already visible - no more zeros.
 *
 * AEO/SEO integrity:
 * - Final value is ALWAYS present from first render (no starting from 0).
 * - Accessible text (aria-label, sr-only span) always shows the final value.
 * - The animated counter is aria-hidden - crawlers and screen readers ignore it.
 */

import React, { useEffect, useRef, useState } from "react";

const MATRIX_CHARS = "0123456789ABCDEF@#$%&*!?+=-<>[]{}|/\\^~";

interface AnimatedStatProps {
  value: number | string;
  prefix?: string;
  suffix?: string;
  label: string;
  sub?: string;
  source?: string;
  isStatic?: boolean;
  size?: "sm" | "md" | "lg";
  accentColor?: string;
  className?: string;
  interactive?: boolean;
}

function easeOutExpo(t: number): number {
  return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
}

function toNumeric(value: number | string): number | null {
  if (typeof value === "number") return value;
  const trimmed = String(value).trim();
  const n = Number(trimmed);
  return !isNaN(n) && trimmed !== "" ? n : null;
}

function randomGlyphs(len: number): string {
  let s = "";
  for (let i = 0; i < len; i++) {
    s += MATRIX_CHARS[Math.floor(Math.random() * MATRIX_CHARS.length)];
  }
  return s;
}

export default function AnimatedStat({
  value,
  prefix = "",
  suffix = "",
  label,
  sub,
  source,
  isStatic = false,
  size = "md",
  accentColor = "#00D4AA",
  className = "",
  interactive = false,
}: AnimatedStatProps) {
  const numeric = isStatic ? null : toNumeric(value);
  const isAnimatable = numeric !== null;

  // finalLabel: always the real value - for aria and sr-only
  const finalLabel = `${prefix}${value}${suffix}`;

  // visualCounter: STARTS at the final value, not 0.
  // Animation will temporarily replace this with glyphs/count-up,
  // but if animation never runs, the real number is always shown.
  const [visualCounter, setVisualCounter] = useState<string | number>(value);
  const [isGlitching, setIsGlitching] = useState(false);

  const hasAnimatedRef = useRef(false);
  const ref = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isAnimatable) {
      setVisualCounter(value);
      return;
    }

    const runAnimation = () => {
      if (hasAnimatedRef.current) return;
      hasAnimatedRef.current = true;

      const to = numeric as number;
      const digitLen = String(Math.round(to)).length;

      // Phase 1: Matrix glyph scramble (300ms - shortened from 400ms)
      const SCRAMBLE_DURATION = 300;
      const SCRAMBLE_INTERVAL = 40;
      setIsGlitching(true);

      let scrambleStart: number | null = null;
      let lastGlyphTime = 0;

      const scrambleTick = (now: number) => {
        if (scrambleStart === null) scrambleStart = now;
        const elapsed = now - scrambleStart;

        if (now - lastGlyphTime >= SCRAMBLE_INTERVAL) {
          setVisualCounter(randomGlyphs(digitLen));
          lastGlyphTime = now;
        }

        if (elapsed < SCRAMBLE_DURATION) {
          rafRef.current = requestAnimationFrame(scrambleTick);
        } else {
          // Phase 2: count-up (1000ms - shortened from 1400ms)
          setIsGlitching(false);
          const COUNT_DURATION = 1000;
          const countStart = performance.now();

          const countTick = (ts: number) => {
            const progress = Math.min((ts - countStart) / COUNT_DURATION, 1);
            const eased = easeOutExpo(progress);
            setVisualCounter(Math.round(to * eased));
            if (progress < 1) {
              rafRef.current = requestAnimationFrame(countTick);
            } else {
              // Ensure final value is exactly right
              setVisualCounter(to);
            }
          };

          rafRef.current = requestAnimationFrame(countTick);
        }
      };

      rafRef.current = requestAnimationFrame(scrambleTick);
    };

    // IntersectionObserver - triggers animation when element enters viewport
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          if (timerRef.current) clearTimeout(timerRef.current);
          runAnimation();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" }
    );

    if (ref.current) observer.observe(ref.current);

    return () => {
      observer.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sizeClasses = {
    sm: { number: "text-3xl md:text-4xl", label: "text-xs", sub: "text-xs" },
    md: { number: "text-4xl md:text-5xl", label: "text-sm", sub: "text-xs" },
    lg: { number: "text-5xl md:text-6xl lg:text-7xl", label: "text-base", sub: "text-sm" },
  }[size];

  const displayColor = isGlitching ? "#00FF41" : accentColor;

  return (
    <div
      ref={ref}
      className={`group relative flex flex-col items-start ${interactive ? "transition-transform duration-200 ease-out hover:-translate-y-0.5 motion-reduce:transform-none motion-reduce:transition-none" : ""} ${className}`}
      style={{ "--accent": accentColor } as React.CSSProperties}
      aria-label={`${finalLabel} - ${label}`}
    >
      {/* Top accent bar */}
      <div
        className="w-8 h-0.5 mb-3 transition-all duration-500 group-hover:w-14"
        style={{ backgroundColor: accentColor }}
      />

      {/* Accessible text: always shows the final value */}
      <span className="sr-only" data-metric-value={finalLabel}>
        {finalLabel}
      </span>

      {/* Visual animated counter */}
      <div
        className={`stat-number leading-none transition-colors duration-150 ${sizeClasses.number}`}
        style={{
          color: displayColor,
          textShadow: isGlitching
            ? `0 0 8px #00FF4180, 0 0 20px #00FF4140`
            : "none",
          fontFamily: isGlitching ? "'JetBrains Mono', monospace" : undefined,
          letterSpacing: isGlitching ? "0.05em" : undefined,
        }}
        aria-hidden="true"
      >
        {!isGlitching && (
          <span className="text-[0.6em] align-top mt-1 inline-block opacity-80">{prefix}</span>
        )}
        <span>{visualCounter}</span>
        {!isGlitching && (
          <span className="text-[0.55em] align-baseline ml-0.5 opacity-90">{suffix}</span>
        )}
      </div>

      {/* Label */}
      <p className={`mt-2 font-medium text-white/90 leading-snug ${sizeClasses.label}`}>
        {label}
      </p>

      {/* Sub-label */}
      {sub && (
        <p className={`mt-0.5 text-white/40 ${sizeClasses.sub}`}>{sub}</p>
      )}

      {/* Source footnote */}
      {source && (
        <p className="mt-1 text-white/25 text-[10px] italic">{source}</p>
      )}
    </div>
  );
}

/**
 * AnimatedStatGrid - wraps multiple AnimatedStat in a responsive grid
 */
interface StatItem {
  value: number | string;
  prefix?: string;
  suffix?: string;
  label: string;
  sub?: string;
  source?: string;
  isStatic?: boolean;
}

interface AnimatedStatGridProps {
  stats: StatItem[];
  title?: string;
  eyebrow?: string;
  cols?: 2 | 3 | 4;
  accentColor?: string;
  dark?: boolean;
  className?: string;
  interactive?: boolean;
}

export function AnimatedStatGrid({
  stats,
  title,
  eyebrow,
  cols = 4,
  accentColor = "#00D4AA",
  dark = true,
  className = "",
  interactive = false,
}: AnimatedStatGridProps) {
  const gridCols = {
    2: "grid-cols-2",
    3: "grid-cols-2 md:grid-cols-3",
    4: "grid-cols-2 md:grid-cols-4",
  }[cols];

  return (
    <div
      className={`relative rounded-sm ${interactive ? "transition-[transform,box-shadow] duration-300 ease-out hover:-translate-y-0.5 hover:shadow-[0_18px_42px_rgba(0,212,170,0.16)] motion-reduce:transform-none motion-reduce:transition-none" : ""} ${className}`}
      style={{
        background: dark
          ? "linear-gradient(135deg, #0a1628 0%, #0d1f3c 50%, #0a1628 100%)"
          : "linear-gradient(135deg, #f0fdf9 0%, #e6fff8 50%, #f0fdf9 100%)",
        border: `1px solid ${accentColor}22`,
        boxShadow: `0 0 40px ${accentColor}08, inset 0 1px 0 ${accentColor}15`,
      }}
    >
      {/* Corner accent glow */}
      <div
        className="absolute top-0 right-0 w-48 h-48 rounded-full pointer-events-none"
        style={{
          background: `radial-gradient(circle, ${accentColor}12 0%, transparent 70%)`,
          transform: "translate(30%, -30%)",
        }}
      />

      <div className="relative z-10 p-6 md:p-8 lg:p-10">
        {(eyebrow || title) && (
          <div className="mb-8">
            {eyebrow && (
              <span
                className="text-mono text-xs uppercase tracking-widest block mb-2"
                style={{ color: accentColor }}
              >
                {eyebrow}
              </span>
            )}
            {title && (
              <h3 className={`font-serif text-xl md:text-2xl ${dark ? "text-white" : "text-slate-900"}`}>
                {title}
              </h3>
            )}
          </div>
        )}

        <div className={`grid ${gridCols} gap-6 md:gap-8 lg:gap-10`}>
          {stats.map((stat, i) => (
            <AnimatedStat
              key={i}
              {...stat}
              accentColor={accentColor}
              size="md"
              interactive={interactive}
            />
          ))}
        </div>
      </div>

      {/* Bottom accent line */}
      <div
        className="absolute bottom-0 left-0 right-0 h-px"
        style={{
          background: `linear-gradient(90deg, transparent, ${accentColor}40, transparent)`,
        }}
      />
    </div>
  );
}

// ─── MatrixNumber ────────────────────────────────────────────────────────────
// Lightweight inline component for raw stat strings (e.g. "+350%", "4.8x").
// ALWAYS shows the final value immediately - animation is progressive enhancement.

const MATRIX_GLYPHS = "0123456789ABCDEF@#$%&*!?+=-<>[]{}|/\\^~";

interface MatrixNumberProps {
  value: string;
  className?: string;
}

export function MatrixNumber({ value, className = "" }: MatrixNumberProps) {
  // Start with the FINAL value - never 0
  const [display, setDisplay] = React.useState(value);
  const [isMatrix, setIsMatrix] = React.useState(false);
  const ref = React.useRef<HTMLSpanElement>(null);
  const animRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasRun = React.useRef(false);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const run = () => {
      if (hasRun.current) return;
      hasRun.current = true;

      // Phase 1: Matrix scramble for 300ms
      setIsMatrix(true);
      const start = Date.now();
      const scramble = () => {
        const elapsed = Date.now() - start;
        if (elapsed < 300) {
          const rand = Array.from({ length: value.length }, () =>
            MATRIX_GLYPHS[Math.floor(Math.random() * MATRIX_GLYPHS.length)]
          ).join("");
          setDisplay(rand);
          animRef.current = setTimeout(scramble, 45);
        } else {
          // Phase 2: count-up for 1000ms
          setIsMatrix(false);
          const countStart = Date.now();
          const numMatch = value.match(/[\d.]+/);
          const finalNum = numMatch ? parseFloat(numMatch[0]) : null;
          const pfx = numMatch ? value.slice(0, numMatch.index) : "";
          const sfx = numMatch ? value.slice((numMatch.index ?? 0) + numMatch[0].length) : "";

          if (finalNum === null) {
            setDisplay(value);
            return;
          }

          const countUp = () => {
            const t = Math.min((Date.now() - countStart) / 1000, 1);
            const ease = t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
            const current = finalNum * ease;
            const formatted = Number.isInteger(finalNum)
              ? Math.round(current).toString()
              : current.toFixed(1);
            setDisplay(`${pfx}${formatted}${sfx}`);
            if (t < 1) {
              animRef.current = setTimeout(countUp, 16);
            } else {
              setDisplay(value);
            }
          };
          countUp();
        }
      };
      scramble();
    };

    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) run(); },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" }
    );
    observer.observe(el);

    return () => {
      observer.disconnect();
      if (animRef.current) clearTimeout(animRef.current);
    };
  }, [value]);

  return (
    <span
      ref={ref}
      className={className}
      style={isMatrix ? { color: "#00FF41", textShadow: "0 0 8px #00FF41, 0 0 16px #00FF4180", fontFamily: "monospace" } : undefined}
    >
      {display}
    </span>
  );
}
